import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContextProvider";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Menu,
  MenuItem,
  Autocomplete,
  TextField,
  Stack,
  CircularProgress,
  Chip,
  Tooltip,
  Checkbox,
  Popper,
  Popover,
  Divider,
  Skeleton,
  FormControl,
  Select,
  ListItemText,
  Radio,
  InputLabel,
  Grid,
  TableContainer,
  Paper,
  InputAdornment,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { Icon } from "@iconify/react";
import moment from "moment";
import { toast } from "sonner";
import { BlankPreview } from "../../components/BlankPreview";
import nopreview from "../../assets/images/others/no-preview.png";
import { useGetCases, exportCases } from "../../api/cases";
import { useSetCaseInvite, useSetCloseCase } from "../../api/cases";
import { useGetPeoples } from "../../api/settings";
import { RequireAuth } from "../../RequireAuth";
import { acceptNotification, rejectNotification } from "../../api/notification";
import { endpoints } from "../../utils/axios";
import { ANALYTICS_EVENTS, logAnalyticsEvent } from "../../analytics";
import { mutate } from "swr";
import Swal from "sweetalert2";
import CustomFilters from "../../components/Filters/Filters";
import TypeIcon from "../../components/Reusables/HubSpokeIcon/TypeIcon";
import UploadReportModal from "../../components/Reusables/UploadModel/UploadReportModal";
import { CloseCaseJourneyDialog } from "../../components/Reusables/PatientJourney";

/**
 * closure_outcome is the closing note. The API records it on the case's
 * CLOSED_AT log entry, which is read first; the note just submitted is the
 * fallback for responses that do not echo the case back. Whatever comes out is
 * clamped to 100 characters by the analytics layer before it is sent.
 */
const getClosureNote = (response, submittedNote) => {
  const closedCase = response?.data || response;
  const logs = Array.isArray(closedCase?.logs) ? closedCase.logs : [];

  // Last CLOSED_AT wins: a reopened-and-reclosed case carries more than one.
  const closedLog = [...logs]
    .reverse()
    .find((log) => log?.action === "CLOSED_AT" && log?.note);

  return closedLog?.note || closedCase?.note || submittedNote || undefined;
};

const CustomPopper = (props) => {
  const { anchorEl } = props;

  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        { name: "offset", options: { offset: [0, 6] } },
        { name: "preventOverflow", options: { boundary: "viewport" } },
      ]}
      sx={{
        width: anchorEl ? anchorEl.clientWidth : 260,
        minWidth: anchorEl ? anchorEl.clientWidth : 260,
        zIndex: 1500,
        "& .MuiAutocomplete-listbox": {
          maxHeight: 240,
        },
      }}
    />
  );
};

function ReferredCases() {
  const navigate = useNavigate();
  const { user, all_centres, selectedCenter, onReceivePushNotification } =
    useContext(AuthContext);
  const [status, setStatus] = useState(() => {
    return sessionStorage.getItem("referredCasesStatus") || "active";
  });

  useEffect(() => {
    sessionStorage.setItem("referredCasesStatus", status);
  }, [status]);
  const [refreshArg, setRefreshArg] = useState(false);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [selectedHubIds, setSelectedHubIds] = useState([]);
  const [selectedSpokeIds, setSelectedSpokeIds] = useState([]);
  const [filters, setFilters] = useState({
    sorting: "desc",
    status: "",
    testResult: "",
    dateFrom: "",
    dateTo: "",
    searchText: "",
    centerIds: "",
    doctorIds: "",
  });

  const isB2BAdmin = user?.userType === "b2b-admin";

  const { cases, isLoading } = useGetCases({
    status,
    sorting: filters.sorting,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    searchText: filters.searchText || "",
    centers: filters.centerIds ? JSON.parse(filters.centerIds) : undefined,
    doctorIds: filters.doctorIds ? JSON.parse(filters.doctorIds) : undefined,
    refreshArg,
  });

  const [casesData, setCasesData] = useState(() =>
    Array.isArray(cases) ? cases : [],
  );

  useEffect(() => {
    if (cases) {
      setCasesData(cases);
    }
  }, [cases]);

  useEffect(() => {
    if (onReceivePushNotification) {
      setRefreshArg((p) => !p);
    }
  }, [onReceivePushNotification]);

  const hasRole = (allowedRoles) => allowedRoles.includes(user?.userType);
  const hubOptions = useMemo(() => {
    if (!Array.isArray(all_centres)) return [];
    return all_centres.map((hub) => ({
      label: hub.label,
      value: hub.value,
    }));
  }, [all_centres]);

  /**
   * The cases API returns each referral side as a NAME plus a type ("hub" /
   * "spoke"), never an id, so the centre ids the analytics events need are
   * resolved against the organisation's own centre list. A name that matches
   * two centres, or a centre outside this organisation, resolves to nothing —
   * an absent id is filterable in GA4, a wrong one silently corrupts the funnel.
   */
  const centreIdByName = useMemo(() => {
    const index = new Map();
    const add = (name, id) => {
      if (!name || !id) return;
      const key = String(name).trim().toLowerCase();
      // null marks "ambiguous": seen more than once, so never resolvable.
      index.set(key, index.has(key) ? null : id);
    };

    (all_centres || []).forEach((hub) => {
      add(hub.label, hub.value);
      (hub.spokeCenters || []).forEach((spoke) => add(spoke.name, spoke.id));
    });

    return index;
  }, [all_centres]);

  const resolveCentreIds = useCallback(
    (caseRow) => {
      const lookup = (name) =>
        centreIdByName.get(String(name || "").trim().toLowerCase()) || undefined;

      const sides = [
        { type: caseRow?.referred_by_center_type, name: caseRow?.referred_by_center_name },
        { type: caseRow?.referred_to_center_type, name: caseRow?.referred_to_center_name },
      ];

      // Keyed off the type the API states, not off which field it arrived in.
      return {
        spoke_centre_id: lookup(sides.find((side) => side.type === "spoke")?.name),
        hub_centre_id: lookup(sides.find((side) => side.type === "hub")?.name),
      };
    },
    [centreIdByName],
  );

  const spokeOptions = useMemo(() => {
    if (!Array.isArray(all_centres) || selectedHubIds.length === 0) return [];

    return all_centres
      .filter((hub) => selectedHubIds.includes(hub.value)) // FIX
      .flatMap((hub) =>
        (hub.spokeCenters || []).map((spoke) => ({
          label: spoke.name,
          value: spoke.id,
          hubValue: hub.value, // optional, but correct
        })),
      );
  }, [all_centres, selectedHubIds]);

  // useEffect(() => {
  //   if (centreOptions.length && selectedCentres.length === 0) {
  //     setSelectedCentres(centreOptions);
  //   }
  // }, [centreOptions, selectedCentres.length]);

  const handleOnDoctorChange = async ({
    reportId,
    invitedDoctors = [],
    removedDoctors = [],
  }) => {
    if (!reportId) return;

    try {
      const response = await useSetCaseInvite(reportId, {
        invitedDoctors,
        removedDoctors,
      });

      // One event per doctor, so the event count is the number of invites
      // rather than the number of times the picker was saved.
      invitedDoctors.forEach((doctorId) => {
        logAnalyticsEvent(ANALYTICS_EVENTS.DOCTOR_INVITED_TO_CASE, {
          case_id: reportId,
          doctor_id: doctorId,
          source: "referred_cases_list",
        });
      });

      removedDoctors.forEach((doctorId) => {
        logAnalyticsEvent(ANALYTICS_EVENTS.DOCTOR_REMOVED_FROM_CASE, {
          case_id: reportId,
          doctor_id: doctorId,
          source: "referred_cases_list",
        });
      });

      toast.success(response?.message || "Doctor updated successfully");

      // 🔄 Optimistic UI update
      setCasesData((prev) =>
        prev.map((c) => {
          if (String(c.report_id) !== String(reportId)) return c;

          let updatedDoctors = [...(c.doctors || [])];

          // ➕ add invited doctors
          invitedDoctors.forEach((id) => {
            if (!updatedDoctors.some((d) => d._id === id)) {
              updatedDoctors.push({ _id: id });
            }
          });

          // ➖ remove uninvited doctors
          updatedDoctors = updatedDoctors.filter(
            (d) => !removedDoctors.includes(d._id),
          );

          return { ...c, doctors: updatedDoctors };
        }),
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to update doctor selection.");
    }
  };

  const handleCloseCase = async (reportId, note = "") => {
    if (!reportId) return;

    try {
      const response = await useSetCloseCase(reportId, { note });

      if (response?.success) {
        const closedCase = (casesData || []).find(
          (item) => String(item.report_id) === String(reportId),
        );

        logAnalyticsEvent(ANALYTICS_EVENTS.REFERRED_CASE_CLOSED, {
          case_id: reportId,
          ...resolveCentreIds(closedCase),
          closure_outcome: getClosureNote(response, note),
        });
      }

      toast.success(response?.message || "Case closed successfully.");

      setCasesData((prev) =>
        prev.map((c) =>
          c.report_id === reportId ? { ...c, status: "closed" } : c,
        ),
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to close case.");
    }
  };

  useEffect(() => {
    if (isB2BAdmin && hubOptions.length > 0) {
      const allHubIds = hubOptions.map((h) => h.value);

      setSelectedHubIds(allHubIds);
      setSelectedSpokeIds([]);

      setFilters((prev) => ({
        ...prev,
        centerIds: JSON.stringify(allHubIds),
      }));
    }
  }, [isB2BAdmin, hubOptions]);

  const downloadCSV = (rows, filename = "referred-cases.csv") => {
    if (!rows.length) return;

    const escape = (v) =>
      `"${String(v ?? "")
        .replace(/"/g, '""')
        .replace(/\n/g, " ")}"`;

    const headers = Object.keys(rows[0]).join(",");

    const csvBody = rows
      .map((row) => Object.values(row).map(escape).join(","))
      .join("\n");

    const csv = `${headers}\n${csvBody}`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCustomFilterApply = (cf) => {
    // searchText is driven by the standalone search box below, not by
    // CustomFilters (which has no search field of its own) — leave it alone
    // here so it isn't reset to "" on every other filter change.
    setFilters((prev) => ({
      ...prev,
      sorting: cf.sort || prev.sorting,
      dateFrom: cf.dateFrom || "",
      dateTo: cf.dateTo || "",
      centerIds: JSON.stringify(cf.spokeIds?.length ? cf.spokeIds : cf.hubIds),
      doctorIds: cf.doctorIds?.length ? JSON.stringify(cf.doctorIds) : "",
    }));
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Referred Cases</title>
      </Helmet>
      <div className="main-wrappers">
        <div
          className="page-wrapper"
          style={{
            maxHeight: "99vh",
            overflowY: "auto",
            paddingBottom: "10px",

            fontFamily: "Figtree",
            backgroundColor: "var(--app-bg)",
          }}
        >
          <Box
            sx={{
              maxHeight: "99vh",
              overflowY: "auto",
              py: 4,
              px: 3,
              fontFamily: "Figtree",
            }}
          >
            {selectedCenter != null && all_centres.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  bgcolor: "#FFF3CD",
                  p: 2,
                  borderRadius: 2,
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    color: "var(--app-text)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Icon icon="material-symbols:error" width="28" height="28" />
                  &nbsp; You haven’t added any centres to your organisation yet.
                </Typography>
                <Link to="/add-centre" style={{ textDecoration: "none" }}>
                  <Typography
                    sx={{ fontWeight: 700, color: "var(--app-text)" }}
                  >
                    Add a Centre
                  </Typography>
                </Link>
              </Box>
            )}

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                mb: 2,
                fontFamily: "Figtree",
              }}
            >
              Referred Cases
            </Typography>

            <Tabs
              value={status}
              onChange={(e, val) => setStatus(val)}
              textColor="inherit"
              indicatorColor="primary"
              TabIndicatorProps={{
                style: {
                  height: "3px",
                  backgroundColor: "#5751E9",
                },
              }}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 2,
                "& .MuiTab-root": {
                  color: "var(--app-text)",
                  textTransform: "none",
                  fontSize: "15px",
                  fontWeight: 400,
                  minWidth: "auto",
                  paddingRight: "25px",
                  paddingLeft: "25px",
                  fontFamily: "Figtree",
                },
                "& .Mui-selected": {
                  fontWeight: 700,
                  color: "var(--app-text)",
                },
              }}
            >
              <Tab label="Open Cases" value="active" />
              {hasRole(["b2b", "b2b-admin"]) && (
                <Tab label="Resolved Cases" value="closed" />
              )}
            </Tabs>

            <Box mt={2}>
              <CaseStatus
                casesData={casesData}
                isLoading={isLoading}
                setCasesData={setCasesData}
                user={user}
                handleOnDoctorChange={handleOnDoctorChange}
                handleCloseCase={handleCloseCase}
                resolveCentreIds={resolveCentreIds}
                status={status}
                navigate={navigate}
                filters={filters}
                setFilters={setFilters}
                selectedHubIds={selectedHubIds}
                setSelectedHubIds={setSelectedHubIds}
                selectedSpokeIds={selectedSpokeIds}
                setSelectedSpokeIds={setSelectedSpokeIds}
                hubOptions={hubOptions}
                spokeOptions={spokeOptions}
                handleCustomFilterApply={handleCustomFilterApply}
              />
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
}

/* -----------------------------------------------------------------------------
   STAGE - which state the case is in right now
----------------------------------------------------------------------------- */

/**
 * getCases sends the whole checklist on `case.stage` - every stage with its
 * status and completion time. The list draws only the one that is open, because
 * that is the state the case is in; the rest is there for the case details page
 * and for any client that wants the timeline without a second request.
 *
 * Clicking opens case details, where the full journey already lives.
 */
const StageCell = ({ stages, isClosed, isLoading, onOpen }) => {
  const current = (stages || []).find((s) => s?.status === "in_progress");

  /* A closed case is not sitting at a stage any more - "Closed" is its state. */
  const state = isClosed ? "Closed" : current?.label || null;

  if (!state) {
    return isLoading ? (
      <Skeleton height={20} width={110} />
    ) : (
      <Typography
        sx={{
          fontSize: "15px",
          fontFamily: "Figtree",
          color: "var(--app-text)",
        }}
      >
        --
      </Typography>
    );
  }

  return (
    <Typography
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      title={`Open case details - ${state}`}
     sx={{
  display: "inline-block",
  fontSize: "15px",
  fontWeight: 600,
  fontFamily: "Figtree",
  color: "var(--app-text)",
  cursor: "pointer",
  userSelect: "none",
  outline: "none",
  textDecoration: "underline",
}}
    >
      {state}
    </Typography>
  );
};

const CaseStatus = ({
  casesData,
  isLoading,
  setCasesData,
  user,
  handleOnDoctorChange,
  handleCloseCase,
  resolveCentreIds,
  status,
  navigate,
  filters,
  setFilters,
  selectedHubIds,
  setSelectedHubIds,
  selectedSpokeIds,
  setSelectedSpokeIds,
  hubOptions,
  spokeOptions,
  handleCustomFilterApply,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [loadingMap, setLoadingMap] = useState({});
  // const [openCaseDetails, setOpenCaseDetails] = useState(false);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedDateLabel, setSelectedDateLabel] = useState("Last 7 Days");
  const { peoples: doctorsList = [], isLoading: doctorsLoading } =
    useGetPeoples({
      role: "b2b-doctor",
      isActiveOnly: false,
    });

  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const isCaseOpened = selectedCase?.status !== "closed";

  const resetCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCase(null);
    setReportId(null);
  };

  const openDateMenu = Boolean(dateAnchorEl);
  const today = new Date();
  const filterPillSx = {
    minWidth: 150,
    "& .MuiOutlinedInput-root": {
      height: 38,
      borderRadius: "8px",
      backgroundColor: "var(--app-surface)",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
      "& .MuiSelect-select": {
        fontSize: "14px",
        padding: "10px 12px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "var(--app-text)", // optional: smaller label
    },
  };

  const DropdownIcon = (props) => (
    <Icon icon="eva:arrow-ios-downward-fill" width={18} {...props} />
  );

  useEffect(() => {
    if (selectedSpokeIds.length === 0 && selectedHubIds.length > 0) {
      setFilters((prev) => ({
        ...prev,
        centerIds: JSON.stringify(selectedHubIds),
      }));
    }
  }, [selectedSpokeIds, selectedHubIds]);

  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const defaultFromDate = new Date(startOfToday.getTime() - 6 * 86400000);
  const defaultToDate = startOfToday;

  const addMonths = (date, months) =>
    new Date(date.getFullYear(), date.getMonth() - months, date.getDate());

  const ranges = [
    { label: "Today", from: startOfToday, to: startOfToday },
    {
      label: "Last 7 Days",
      from: new Date(startOfToday.getTime() - 6 * 86400000),
      to: startOfToday,
    },
    {
      label: "Last 30 Days",
      from: new Date(startOfToday.getTime() - 29 * 86400000),
      to: startOfToday,
    },
    {
      label: "Last 3 Months",
      from: addMonths(startOfToday, 3),
      to: new Date(),
    },
    {
      label: "Last 6 Months",
      from: addMonths(startOfToday, 6),
      to: new Date(),
    },
    {
      label: "Last 12 Months",
      from: addMonths(startOfToday, 12),
      to: new Date(),
    },
  ];
  // useEffect(() => {
  //   setFilters((prev) => ({
  //     ...prev,
  //     dateFrom: moment(defaultFromDate).format("DD-MM-YYYY"),
  //     dateTo: moment(defaultToDate).format("DD-MM-YYYY"),
  //   }));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const updateDoctorStatusLocally = (reportId, status) => {
    const loggedId = user?._id;

    setCasesData((prev) =>
      prev.map((c) => {
        if (String(c.report_id) !== String(reportId)) return c;

        return {
          ...c,
          doctors: (c.doctors || []).map((d) =>
            String(d._id) === String(loggedId) ? { ...d, status } : d,
          ),
        };
      }),
    );
  };

  const setLoadingFor = (id, val) => {
    setLoadingMap((prev) => ({ ...prev, [id]: Boolean(val) }));
  };

  const renderSkeletonRows = (columns = 6) =>
    Array.from({ length: 6 }).map((_, rowIdx) => (
      <TableRow key={`skeleton-${rowIdx}`}>
        {Array.from({ length: columns }).map((_, colIdx) => (
          <TableCell key={colIdx}>
            <Skeleton height={24} />
          </TableCell>
        ))}
      </TableRow>
    ));

  const hasOpenCases = casesData.some((c) => c.status !== "closed");

  const handleMenuOpen = (e, caseDt) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget, caseDt);
    setSelectedCase(caseDt);
    setReportId(caseDt?.report_id || null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenCloseDialog = () => {
    if (!reportId && !selectedCase?.report_id) {
      toast.error("Report id not found.");
      return;
    }
    setOpenDialog(true);
    setAnchorEl(null);
  };

  const handleDialogCancel = () => {
    resetCloseDialog();
  };

  /**
   * The journey reached a terminal outcome. The backend closed the case as part
   * of that step, so this only has to catch the list up and record the event.
   *
   * It deliberately does NOT close the popup: the workflow shows its "Case
   * Closed Successfully" confirmation at this point, and dismissing that with
   * "Got It" is what closes the dialog.
   */
  const handleJourneyClosed = (journey) => {
    const idToClose = reportId || selectedCase?.report_id;

    const closedCase = (casesData || []).find(
      (item) => String(item.report_id) === String(idToClose),
    );

    // The case is already closed on the server by this point, so telemetry must
    // never be what stops the list updating or the dialog closing.
    try {
      logAnalyticsEvent(ANALYTICS_EVENTS.REFERRED_CASE_CLOSED, {
        case_id: idToClose,
        ...resolveCentreIds(closedCase),
        closure_outcome: journey?.state?.closeNote || undefined,
      });
    } catch (err) {
      console.error("Failed to log case closure:", err);
    }

    setCasesData((prev) =>
      prev.map((c) =>
        c.report_id === idToClose ? { ...c, status: "closed" } : c,
      ),
    );
  };

  const getLoggedInUserId = () => user?._id ?? null;

  const findDoctorEntryForLoggedIn = (caseDt) => {
    const loggedId = getLoggedInUserId();
    if (!caseDt?.doctors || !Array.isArray(caseDt.doctors)) return null;
    return (
      caseDt.doctors.find((d) => String(d._id) === String(loggedId)) || null
    );
  };

  // Accept handler updates server and then updates local list
  const handleAcceptReferral = async (id) => {
    setLoadingFor(id, true);
    try {
      await acceptNotification({ reportId: id, status: "accepted" });

      //  critical
      updateDoctorStatusLocally(id, "accepted");

      mutate([endpoints.notifications.getNotifications, {}]);

      Swal.fire({
        icon: "success",
        title: "Accepted",
        toast: true,
        position: "top-end",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to accept" });
    } finally {
      setLoadingFor(id, false);
    }
  };

  // Reject handler updates server and local state
  const handleRejectReferral = async (id) => {
    setLoadingFor(id, true);
    try {
      await rejectNotification({ reportId: id, status: "rejected" });

      updateDoctorStatusLocally(id, "rejected");

      mutate([endpoints.notifications.getNotifications, {}]);

      Swal.fire({
        icon: "success",
        title: "Rejected",
        toast: true,
        position: "top-end",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to reject" });
    } finally {
      setLoadingFor(id, false);
    }
  };

  const getDoctorStatus = (caseDt, userId) => {
    const entry = caseDt?.doctors?.find(
      (d) => String(d._id) === String(userId),
    );
    return (entry?.status || "pending").toLowerCase();
  };

  const loggedDoctorId = user?._id;

  const invitationCases = casesData.filter(
    (c) =>
      user?.userType === "b2b-doctor" &&
      getDoctorStatus(c, loggedDoctorId) === "pending",
  );

  const tableCases = casesData.filter((c) => {
    if (user?.userType !== "b2b-doctor") return true;

    const status = getDoctorStatus(c, loggedDoctorId);

    // accepted → show in table
    if (status === "accepted") return true;

    // rejected → hide from table
    return false;
  });

  const goToCaseDetails = (caseDt) => {
    if (!caseDt?.report_id) {
      toast.error("Report id not found.");
      return;
    }

    navigate(`/cases/${caseDt.report_id}`, { state: { caseData: caseDt } });
  };

  const renderStatusChip = (status) => {
    const map = {
      pending: { label: "Pending", color: "default" },
      accepted: { label: "Accepted", color: "success" },
      rejected: { label: "Rejected", color: "error" },
    };

    const cfg = map[status] || map.pending;

    return (
      <Chip
        label={cfg.label}
        color={cfg.color}
        size="small"
        sx={{ fontWeight: 600, borderRadius: "8px" }}
      />
    );
  };
  const downloadCSV = (rows, filename = "referred-cases.csv") => {
    if (!rows.length) return;

    const escape = (v) =>
      `"${String(v ?? "")
        .replace(/"/g, '""')
        .replace(/\n/g, " ")}"`;

    const headers = Object.keys(rows[0]).join(",");
    const csvBody = rows
      .map((row) => Object.values(row).map(escape).join(","))
      .join("\n");

    const csv = `${headers}\n${csvBody}`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    try {
      toast.loading("Preparing CSV...");

      const res = await exportCases({
        status,
        sorting: filters.sorting,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        searchText: filters.searchText || undefined,
        centers: filters.centerIds ? JSON.parse(filters.centerIds) : undefined,
        doctorIds: filters.doctorIds
          ? JSON.parse(filters.doctorIds)
          : undefined,
        export: true,
      });

      const rows = (res?.data || []).map((c) => ({
        "Report ID": c.report_id || c.collection_id,
        "Referred On": c.referred_on
          ? moment(c.referred_on).format("DD/MM/YYYY HH:mm")
          : "",
        "Referred From": c.referred_by_center_name,
        "Referred To": c.referred_to_center_name,
        Patient: c.patient_name,
        Doctor: (c.doctors || []).map((d) => d.fullName).join(", "),
        Status: c.status,
      }));

      if (!rows.length) {
        toast.dismiss();
        toast.info("No data to export");
        return;
      }

      downloadCSV(rows, `referred-cases-${moment().format("YYYY-MM-DD")}.csv`);

      toast.dismiss();
      toast.success("CSV downloaded");
    } catch (err) {
      toast.dismiss();
      console.error(err);
      toast.error("Failed to export CSV");
    }
  };

  const showEmptyState = !isLoading && tableCases.length === 0;
  const dividerStyle = {
    py: 0,
    marginBottom: 0,
    marginTop: 0,
    width: "80%",
    maxWidth: 360,
    borderRadius: 2,
    border: "0.1px solid var(--app-border)",
    borderColor: "divider",
  };

  return (
    <>
      {user?.userType === "b2b-doctor" && invitationCases.length > 0 && (
        <Box mb={4}>
          <Typography fontWeight={700} mb={2}>
            Invitations ({invitationCases.length})
          </Typography>

          {invitationCases.map((caseDt) => {
            const id = caseDt.report_id;

            return (
              <Box
                key={id}
                sx={{
                  background: "var(--app-surface)",
                  borderRadius: 2,
                  p: 2,
                  mb: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "var(--app-shadow)",
                }}
              >
                <Box>
                  <Typography fontWeight={600}>
                    {caseDt.report_id} ({caseDt.patient_name})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {moment(caseDt.referred_on).format("DD/MM/YYYY, hh:mm a")}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={loadingMap[id]}
                    onClick={() => handleAcceptReferral(id)}
                  >
                    Accept
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    disabled={loadingMap[id]}
                    onClick={() => handleRejectReferral(id)}
                  >
                    Reject
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by Case ID, Report ID, Patient Name, Centre Name"
          value={filters.searchText}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, searchText: e.target.value }))
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="gg:search" width="16" height="16" color="var(--app-text-muted)" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 45,
              borderRadius: "9px",
              backgroundColor: "var(--app-surface)",
              "& fieldset": { borderColor: "var(--app-border)" },
            },
          }}
        />
      </Box>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        wrap="nowrap"
        sx={{
          mb: 2,
          mt: 2,
          flexWrap: { xs: "wrap", md: "nowrap" }, // wrap only on mobile
          rowGap: { xs: 1.5, md: 0 },
        }}
      >
        <Grid
          item
          sx={{
            minWidth: 0,
            width: { xs: "100%", md: "auto" }, // full width mobile
            overflow: "hidden", // removes scrollbar issue
          }}
        >
          <CustomFilters
            component="referred-cases"
            onApply={handleCustomFilterApply}
          />
        </Grid>

        <Grid
          item
          sx={{
            width: { xs: "100%", md: "auto" },
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" }, // left on mobile
          }}
        >
          {casesData.length > 0 && (
            <Button
              startIcon={<Icon icon="material-symbols:download" width="20" />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#5751E9",
                fontSize: { xs: "14px", md: "15px" },
                whiteSpace: "nowrap",
              }}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
          )}
        </Grid>
      </Grid>

      {showEmptyState ? (
        <Box sx={{ mt: 6 }}>
          <BlankPreview
            icon={nopreview}
            title="No Referred Cases Available"
            sx={{ backgroundColor: "var(--app-surface-2)" }}
          />
        </Box>
      ) : (
        <TableContainer
          className="app-table-card"
          component={Paper}
          elevation={0}
        >
          <Table
            sx={{
              /* Room for every column at its natural width. Narrower than this
                 and the card scrolls sideways rather than squeezing cells. */
              minWidth: 1180,
              borderCollapse: "separate",
              borderSpacing: "0 12px", // <-- adds 10px gap between rows
              backgroundColor: "var(--app-surface-2)",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "transparent",
                  borderRight: "none",
                  fontFamily: "Figtree",
                  "& th": {
                    paddingBottom: "0px",
                    paddingTop: "0px",
                  },
                }}
              >
                {" "}
                {[
                  "CASE ID",
                  "REFERRED ON",
                  "REFERRED FROM",
                  "REFERRED TO",
                  "PATIENT",
                  // ...(user?.userType === "b2b-doctor" ? ["STATUS"] : []),
                  ...(user?.userType !== "b2b-doctor" ? ["DOCTOR"] : []),
                  "STAGE",
                  // ...(hasOpenCases ? ["ACTION"] : []),
                  "ACTION"
                ].map((head, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontWeight: 800,
                      fontSize: "15px",
                      color: "#A3A3A3",
                      px: "16px",
                      py: 1.5,
                      whiteSpace: "nowrap",
                      backgroundColor: "var(--app-surface-2)",
                      borderBottom: "none",
                      fontFamily: "Figtree",
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading
                ? renderSkeletonRows(
                  6 +
                  (user?.userType !== "b2b-doctor" ? 1 : 0) +
                  (hasOpenCases ? 1 : 0),
                )
                : tableCases.map((caseDt, index) => {
                  const isClosed = caseDt.status === "closed";
                  const doctorNames =
                    caseDt.doctors?.map((d) => d.fullName).join(", ") || "--";
                  return (
                    <TableRow
                      key={caseDt.report_id || caseDt.collection_id || index}
                      sx={{
                        backgroundColor: "var(--app-surface)",
                        borderRadius: "8px",
                        "&:last-child td, &:last-child th": { border: 0 },
                        "& td:first-of-type": {
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                        },
                        "& td": {
                          py: "10px",
                          px: "16px",
                          fontSize: "14px",
                          borderBottom: "0px solid black",
                        },
                        "& td:last-of-type": {
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                        },
                      }}
                    >
                      <TableCell
                        onClick={() =>
                          navigate(`/ecg-reports/${caseDt?.report_id}`)
                        }
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: "15px",
                        }}
                      >
                        {caseDt?.report_id || caseDt?.collection_id || "--"}
                      </TableCell>

                      <TableCell>
                        {caseDt.referred_on
                          ? moment(caseDt.referred_on).format(
                            "DD/MM/YYYY, hh:mm a",
                          )
                          : "--"}
                      </TableCell>

                      <TableCell title={caseDt.referred_by_center_name}>
                        <TypeIcon
                          type={caseDt.referred_by_center_type}
                          size={15}
                        />
                        <span style={{ marginLeft: "6px" }}>
                          {caseDt.referred_by_center_name || "--"}
                        </span>
                      </TableCell>

                      <TableCell title={caseDt.referred_to_center_name}>
                        <TypeIcon
                          type={caseDt.referred_to_center_type}
                          size={15}
                        />
                        <span style={{ marginLeft: "6px" }}>
                          {caseDt.referred_to_center_name || "--"}
                        </span>
                      </TableCell>

                      {/* <TableCell>
                        {caseDt.patient_user_id ? (
                          <Link
                            to={`/patients/${caseDt.patient_user_id}`}
                            style={{
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "15px",
                              color: "var(--app-text)",
                            }}
                          >
                            {caseDt.patient_name
                              ? caseDt.patient_name.length > 10
                                ? caseDt.patient_name.slice(0, 10) + "..."
                                : caseDt.patient_name
                              : "--"}
                          </Link>
                        ) : (
                          <span style={{ fontSize: "15px", color: "var(--app-text)" }}>
                            {caseDt.patient_name
                              ? caseDt.patient_name.length > 10
                                ? caseDt.patient_name.slice(0, 10) + "..."
                                : caseDt.patient_name
                              : "--"}
                          </span>
                        )}
                      </TableCell> */}
                      <TableCell>
                        {caseDt.patient_user_id ? (
                          <Link
                            to={`/patients/${caseDt.patient_user_id}`}
                            style={{
                              textDecoration: "underline",
                              color: "var(--app-text)",
                              display: "block",
                            }}
                          >
                            <Typography sx={{ fontSize: "15px" }}>
                              {caseDt.patient_name || "--"}
                            </Typography>
                          </Link>
                        ) : (
                          <Typography sx={{ fontSize: "15px" }}>
                            {caseDt.patient_name || "--"}
                          </Typography>
                        )}
                      </TableCell>
                      {/* {user?.userType === "b2b-doctor" &&
                  < TableCell >
                    {renderStatusChip(getDoctorStatus(caseDt, loggedDoctorId))}

                  </TableCell>
                } */}

                      {user?.userType !== "b2b-doctor" && (
                        <TableCell sx={{ width: 260 }}>
                          {isClosed ? (
                            doctorNames
                          ) : (
                            <SelectDoctorUI
                              centerId={caseDt?.center_id}
                              reportId={caseDt?.report_id}
                              doctors={caseDt?.doctors}
                              onChange={handleOnDoctorChange}
                            />
                          )}
                        </TableCell>
                      )}

                      <TableCell>
                        <StageCell
                          stages={caseDt?.stage}
                          isClosed={isClosed}
                          isLoading={isLoading}
                          onOpen={() => goToCaseDetails(caseDt)}
                        />
                      </TableCell>

                      {(
                        <TableCell sx={{ px: "16px" }}>
                          <Box display="flex" justifyContent="flex-end">
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, caseDt)}
                            >
                              <Icon icon="mdi:dots-vertical" width="22" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,

            width: {
              xs: "50vw", // mobile
              sm: 210, // tablet+
            },

            maxHeight: {
              xs: "70vh",
              sm: 320,
            },

            overflowY: "auto", // allow scroll if many items
          },
        }}
      >
        {user?.userType !== "b2b-doctor" && isCaseOpened ? (
          <>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose();
                const idToUse = reportId || selectedCase?.report_id;
                if (!idToUse) {
                  toast.error("Report id not found for consult.");
                  return;
                }
                window.open(`/onlineconsultation/${idToUse}`, "_self");
                setSelectedCase(null);
                setReportId(null);
              }}
            >
              <Box display="flex" alignItems="center" gap="8px">
                <Icon icon="mynaui:video-solid" width="18" height="18" />

                <Typography
                  sx={{
                    fontFamily: "Figtree",
                    fontWeight: 600,
                    fontSize: "15px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                  Consult Now
                </Typography>
              </Box>
            </MenuItem>
            <Divider variant="middle" component="li" sx={dividerStyle} />
          </>
        ) : (
          (() => {
            const docEntry = findDoctorEntryForLoggedIn(selectedCase || {});
            const docStatus = (docEntry?.status || "pending").toLowerCase();
            const idToUse = reportId || selectedCase?.report_id;

            if (!idToUse) return null;

            // ACCEPTED → Consult + Reject
            if (docStatus === "accepted") {
              return (
                <>
                  <MenuItem
                    onClick={() =>
                      window.open(`/onlineconsultation/${idToUse}`, "_self")
                    }
                  >
                    <Box display="flex" alignItems="center" gap="8px">
                      <Icon icon="mynaui:video-solid" width="18" height="18" />

                      <Typography
                        sx={{
                          fontFamily: "Figtree",
                          fontWeight: 600,
                          fontSize: "15px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                        }}
                      >
                        Consult Now
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider variant="middle" component="li" sx={dividerStyle} />
                  {/* <MenuItem
                    disabled={loadingMap[idToUse]}
                    onClick={() => handleRejectReferral(idToUse)}
                  >
                    <Icon icon="mdi:close-circle" width="20" color="#e74c3c" />
                    &nbsp; Reject
                  </MenuItem> */}
                </>
              );
            }

            //  REJECTED → Consult only
            if (docStatus === "rejected") {
              return null;
            }

            //  PENDING → Accept + Reject
            if (isCaseOpened && docStatus === "pending") {
              return (
                <>
                  <MenuItem
                    disabled={loadingMap[idToUse]}
                    onClick={() => handleAcceptReferral(idToUse)}
                  >
                    <Icon icon="mdi:check-circle" width="20" color="#2ecc71" />
                    &nbsp; Accept
                  </MenuItem>

                  <MenuItem
                    disabled={loadingMap[idToUse]}
                    onClick={() => handleRejectReferral(idToUse)}
                  >
                    <Icon icon="mdi:close-circle" width="20" color="#e74c3c" />
                    &nbsp; Reject
                  </MenuItem>
                </>
              );
            }

            return null;
          })()
        )}

        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();

            if (!selectedCase) {
              toast.error("No case selected.");
              return;
            }

            navigate(`/cases/${selectedCase.report_id}`, {
              state: { caseData: selectedCase },
            });

            setSelectedCase(null);
            setReportId(null);
          }}
        >
          <Box display="flex" alignItems="center" gap="8px">
            <Icon icon="mingcute:paper-fill" width="18px" height="18px" />
            <Typography
              sx={{
                fontFamily: "Figtree",
                fontWeight: 600,
                fontSize: "15px",
                lineHeight: "100%",
                letterSpacing: "0%",
              }}
            >
              {" "}
              View Case Details
            </Typography>
          </Box>
        </MenuItem>

        {isCaseOpened && (
          <>
            <Divider variant="middle" component="li" sx={dividerStyle} />

            <MenuItem
              onClick={(e) => {
                e.stopPropagation();

                if (!selectedCase?.patient_user_id) {
                  toast.error("Patient id not found");
                  return;
                }

                handleMenuClose();

                setOpenUploadModal(true);
              }}
            >
              <Box display="flex" alignItems="center" gap="8px">
                <Icon icon="material-symbols:upload" width="18" height="18" />
                <Typography
                  sx={{
                    fontFamily: "Figtree",
                    fontWeight: 600,
                    fontSize: "15px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                  {" "}
                  Upload Records
                </Typography>
              </Box>
            </MenuItem>
            <Divider variant="middle" component="li" sx={dividerStyle} />

            <MenuItem
              onClick={(e) => {
                e.stopPropagation();

                handleMenuClose();

                const patientId = selectedCase?.patient_user_id;

                if (!patientId) {
                  toast.error("Patient id not found.");
                  return;
                }

                navigate(`/patients/${patientId}?tab=other_reports`);

                setSelectedCase(null);
                setReportId(null);
              }}
            >
              <Box display="flex" alignItems="center" gap="8px">
                <Icon icon="material-symbols:folder-open" width="18" height="18" />

                <Typography
                  sx={{
                    fontFamily: "Figtree",
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  View Records
                </Typography>
              </Box>
            </MenuItem>
            <Divider variant="middle" component="li" sx={dividerStyle} />

            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose();
                const idToUse = reportId || selectedCase?.report_id;
                if (!idToUse) {
                  toast.error("Report id not found for view report.");
                  return;
                }
                navigate(`/ecg-reports/${idToUse}`);
                setSelectedCase(null);
                setReportId(null);
              }}
            >
              <Box display="flex" alignItems="center" gap="8px">
                <Icon icon="basil:document-solid" width="18px" height="18px" />{" "}
                <Typography
                  sx={{
                    fontFamily: "Figtree",
                    fontWeight: 600,
                    fontSize: "15px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                  {" "}
                  View Report{" "}
                </Typography>
              </Box>
            </MenuItem>
            <Divider variant="middle" component="li" sx={dividerStyle} />

            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClose();
                const patientId = selectedCase?.patient_user_id;
                if (!patientId) {
                  toast.error("Patient id not found.");
                  return;
                }
                navigate(`/patients/${patientId}`);
                setSelectedCase(null);
                setReportId(null);
              }}
            >
              <Box display="flex" alignItems="center" gap="8px">
                <Icon icon="solar:user-bold" width="18px" height="18px" />{" "}
                <Typography
                  sx={{
                    fontFamily: "Figtree",
                    fontWeight: 600,
                    fontSize: "15px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                  {" "}
                  View Patient Profile
                </Typography>
              </Box>
            </MenuItem>
            <Divider variant="middle" component="li" sx={dividerStyle} />

            {["b2b", "b2b-admin", "b2b-doctor"].includes(user?.userType) && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleOpenCloseDialog();
                }}
              >
                <Box display="flex" alignItems="center" gap="8px">
                  <Icon
                    icon="icon-park-solid:check-one"
                    width="18px"
                    height="18px"
                    color="#348530"
                  />{" "}
                  <Typography
                    sx={{
                      fontFamily: "Figtree",
                      fontWeight: 600,
                      fontSize: "15px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      color: "#348530",
                    }}
                  >
                    Close Case
                  </Typography>
                </Box>
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      <UploadReportModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        patientId={selectedCase?.patient_user_id}
      />

      {/* Close Case - the patient journey workflow, same component as the
          case details page */}
      <CloseCaseJourneyDialog
        open={openDialog}
        caseData={selectedCase}
        onClose={handleDialogCancel}
        onClosed={handleJourneyClosed}
      />

      {/* Case Details Popup */}
      {/* < CaseDetailsDialog
        open={openCaseDetails}
        onClose={() => {
          setOpenCaseDetails(false);
          setSelectedCase(null);
          setReportId(null);
        }}
        caseDt={selectedCase || {}}
        peoples={[]} // optionally pass peoples list if you have it in scope
        isLoadingPeoples={false}
      /> */}
    </>
  );
};

// CaseDetailsDialog - improved to match provided UI
// const CaseDetailsDialog = ({
//   open,
//   onClose,
//   caseDt = {},
//   peoples = [],
//   isLoadingPeoples = false,
//   onDoctorChange = () => { },
// }) => {
//   // safe derives
//   // Earth radius in km
//   const EARTH_RADIUS_KM = 6371;

//   // degrees -> radians
//   const toRad = (deg) => (deg * Math.PI) / 180;

//   // Haversine distance in km between two lat/lng points
//   const getDistanceKm = (lat1, lng1, lat2, lng2) => {
//     const φ1 = toRad(lat1);
//     const φ2 = toRad(lat2);
//     const Δφ = toRad(lat2 - lat1);
//     const Δλ = toRad(lng2 - lng1);

//     const a =
//       Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
//       Math.cos(φ1) * Math.cos(φ2) *
//       Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

//     const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
//     console.log("Distance in km:", EARTH_RADIUS_KM * c);
//     return EARTH_RADIUS_KM * c; // distance in km
//   };

//   const reportId = caseDt?.report_id || caseDt?.collection_id || "--";
//   const collectionId = caseDt?.collection_id || caseDt?.report_id || "--";
//   const patient = caseDt?.patient_name || "--";
//   const referredOn = caseDt?.referred_on
//     ? moment(caseDt.referred_on).format("DD/MM/YYYY, hh:mm a")
//     : "--";
//   const spokeName = caseDt?.referred_by_center_name || "--";
//   const hubName = caseDt?.referred_to_center_name || "--";

//   const byLat = caseDt?.referred_by_center_lat;
//   const byLng = caseDt?.referred_by_center_lng;
//   const toLat = caseDt?.referred_to_center_lat;
//   const toLng = caseDt?.referred_to_center_lng;

//   let distanceLabel = "—";

//   if (
//     byLat != null && byLng != null &&
//     toLat != null && toLng != null &&
//     byLat !== "" && byLng !== "" &&
//     toLat !== "" && toLng !== ""
//   ) {
//     const d = getDistanceKm(
//       parseFloat(byLat),
//       parseFloat(byLng),
//       parseFloat(toLat),
//       parseFloat(toLng)
//     );

//     if (!Number.isNaN(d) && Number.isFinite(d)) {
//       distanceLabel = `${d.toFixed(3)} km`;
//     }
//   } else if (caseDt?.distance_text) {
//     distanceLabel = caseDt.distance_text;
//   }

//   // local selected doctors (keeps same shape as SelectDoctorUI expects)
//   const [localDoctors, setLocalDoctors] = useState([]);
//   useEffect(() => {
//     if (Array.isArray(caseDt?.doctors)) {
//       setLocalDoctors(caseDt.doctors.map((d) => d._id));
//     } else {
//       setLocalDoctors([]);
//     }
//   }, [caseDt]);

//   // when selecting doctors in dialog, call provided handler
//   const handleDoctorsChange = ({ doctorId, reportId: rid }) => {
//     if (onDoctorChange) onDoctorChange({ doctorId, reportId: rid });
//     setLocalDoctors(Array.isArray(doctorId) ? doctorId : []);
//   };

//   const handleConsultNow = () => {
//     const id = caseDt?.report_id || caseDt?.collection_id;
//     if (!id) {
//       toast.error("Report id not found for consult.");
//       return;
//     }
//     // open same tab as in your app
//     window.open(`/onlineconsultation/${id}`, "_self");
//     if (onClose) onClose();
//   };
//   const formatPhone = (phone) => {
//     if (!phone) return "-";

//     const cleaned = String(phone).trim();

//     return cleaned.startsWith("+")
//       ? cleaned
//       : `+91${cleaned}`;
//   };

//   return (
//     <Dialog
//       open={Boolean(open)}
//       onClose={onClose}
//       fullWidth
//       PaperProps={{
//         sx: {
//           width: 800,           // desired fixed width in px
//           maxWidth: "95vw",     // responsive fallback for small screens
//           borderRadius: 2,
//           p: 0,
//           overflow: "visible",
//           boxShadow: 24,
//         },
//       }}

//     >
//       <DialogTitle sx={{ fontWeight: 700, px: 4, py: 2, fontSize: 20 }}>
//         Case Details
//         <IconButton
//           aria-label="close"
//           onClick={onClose}
//           sx={{
//             position: "absolute",
//             right: 12,
//             top: 12,
//             color: "text.secondary",
//           }}
//         >
//           <Icon icon="mdi:close" width="20" />
//         </IconButton>
//       </DialogTitle>

//       <DialogContent dividers sx={{ px: { xs: 3, sm: 4 }, py: { xs: 2, sm: 3 } }}>
//         {/* Top row: REPORT ID | COLLECTION ID | PATIENT */}
//         <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
//           <Box sx={{ flex: 1, minWidth: 220 }}>
//             <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>REPORT ID</Typography>
//             <Typography sx={{ textDecoration: "underline", mt: 0.5, fontWeight: 600, cursor: "pointer" }}>{reportId}</Typography>
//           </Box>

//           <Box sx={{ flex: 1, minWidth: 220 }}>
//             <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>COLLECTION ID</Typography>
//             <Typography sx={{ textDecoration: "underline", mt: 0.5, fontWeight: 600 }}>{collectionId}</Typography>
//           </Box>

//           <Box sx={{ flex: 1, minWidth: 220 }}>
//             <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>PATIENT</Typography>
//             <Typography sx={{ textDecoration: "underline", mt: 0.5, fontWeight: 600, cursor: "pointer" }}>{patient}</Typography>
//           </Box>
//         </Box>

//         {/* Referred On */}
//         <Box sx={{ mt: 3 }}>
//           <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>REFERRED ON</Typography>
//           <Typography sx={{ mt: 0.5 }}>{referredOn}</Typography>
//         </Box>

//         {/* Spoke -> Hub row with dotted path */}
//         <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 3 }}>
//           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//             <Icon icon="mdi:home" width="16" />
//             <Typography sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}>{spokeName}</Typography>
//           </Box>

//           <Box sx={{ textAlign: "center", flex: 1, mx: 2 }}>
//             {/* dotted line with center distance and arrow on right */}
//             <Box sx={{ position: "relative", width: "100%" }}>
//               <Box
//                 sx={{
//                   borderBottom: "2px dotted #c9bdfa",
//                   height: 0,
//                   mx: "10px",
//                 }}
//               />
//               <Typography variant="caption" sx={{ position: "absolute", left: "50%", top: -10, transform: "translateX(-50%)", bgcolor: "background.paper", px: 1, color: "text.secondary" }}>
//                 {distanceLabel && `${distanceLabel}`}
//               </Typography>
//             </Box>
//           </Box>

//           <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//             <Icon icon="mdi:office-building" width="16" />
//             <Typography sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}>{hubName}</Typography>
//           </Box>
//         </Box>

//         {/* Contact Details section */}
//         <Box sx={{ mt: 4 }}>
//           <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Contact Details</Typography>

//           <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
//             {/* Hub Admin */}
//             <Box sx={{ flex: 1, minWidth: 200 }}>
//               <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>HUB ADMIN</Typography>
//               <Typography sx={{ mt: 0.5, fontWeight: 600 }}>{caseDt?.referred_to_admin_name}</Typography>
//               <Typography variant="caption" color="text.secondary">{formatPhone(caseDt?.referred_to_admin_phone)}</Typography>
//             </Box>

//             {/* Spoke Admin */}
//             <Box sx={{ flex: 1, minWidth: 200 }}>
//               <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>SPOKE ADMIN</Typography>
//               <Typography sx={{ mt: 0.5, fontWeight: 600 }}>{caseDt?.referred_by_admin_name}</Typography>
//               <Typography variant="caption" color="text.secondary">{formatPhone(caseDt?.referred_by_admin_phone)}</Typography>
//             </Box>

//             {/* Collection Agent */}
//             <Box sx={{ flex: 1, minWidth: 200 }}>
//               <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>COLLECTION AGENT</Typography>
//               <Typography sx={{ mt: 0.5, fontWeight: 600 }}>{caseDt?.collection_agent}</Typography>
//               <Typography variant="caption" color="text.secondary">{formatPhone(caseDt?.collection_agent_phone)}</Typography>
//             </Box>
//           </Box>
//         </Box>

//         {/* Consultation Details */}
//         <Box sx={{ mt: 4 }}>
//           <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Consultation Details</Typography>

//           <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Doctors</Typography>

//           {/* Use existing multi-select UI - reuse SelectDoctorUI to keep consistent */}
//           <Box sx={{ mb: 2 }}>
//             <SelectDoctorUI
//               centerId={caseDt?.center_id}
//               reportId={caseDt?.report_id}
//               doctors={caseDt?.doctors}
//               onChange={({ doctorId, reportId }) => handleDoctorsChange({ doctorId, reportId })}
//             />
//           </Box>

//           <Button
//             variant="contained"
//             fullWidth
//             onClick={handleConsultNow}
//             startIcon={<Icon icon="mynaui:video-solid" width={18} />}
//             sx={{
//               background: "#5B46F2",
//               color: "#fff",
//               py: 1.6,
//               fontWeight: 700,
//               borderRadius: 2,
//               "&:hover": { background: "#4a36c9" },
//             }}
//           >
//             <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
//               Consult Now
//             </Box>
//           </Button>
//         </Box>
//       </DialogContent>

//       {/* <DialogActions sx={{ px: 4, py: 2 }}>
//         <Button onClick={onClose} color="inherit">Close</Button>
//       </DialogActions> */}
//     </Dialog>
//   );
// };

export const SelectDoctorUI = ({
  centerId,
  reportId,
  doctors,
  onChange,
  width = 260,
}) => {
  const { peoples, isLoading } = useGetPeoples({
    role: "b2b-doctor",
    centerIds: [centerId],
  });
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const prevDoctorsRef = React.useRef([]);

  useEffect(() => {
    if (Array.isArray(doctors)) {
      const ids = doctors.map((d) => d._id);
      setSelectedDoctors(ids);
      prevDoctorsRef.current = ids;
    }
  }, [doctors]);

  const handleChange = (event, newValue) => {
    const prevIds = prevDoctorsRef.current;
    const newIds = newValue.map((doc) => doc._id);

    const invitedDoctors = newIds.filter((id) => !prevIds.includes(id));
    const removedDoctors = prevIds.filter((id) => !newIds.includes(id));

    setSelectedDoctors(newIds);
    prevDoctorsRef.current = newIds;

    if (invitedDoctors.length > 0 || removedDoctors.length > 0) {
      onChange({
        reportId,
        invitedDoctors,
        removedDoctors,
      });
    }
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const value = (peoples || []).filter((p) => selectedDoctors.includes(p._id));

  return (
    <Autocomplete
      multiple
      PopperComponent={CustomPopper}
      disableCloseOnSelect
      options={peoples || []}
      getOptionLabel={(option) => option.fullName || ""}
      value={value}
      onChange={handleChange}
      loading={isLoading}
      size="medium"
      isOptionEqualToValue={(option, value) => option._id === value._id}
      popupIcon={
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 20,
            color: "var(--app-text)",
          }}
        />
      }
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option._id}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={selected}
            sx={{ marginRight: 1.2, fontSize: 14, alignItems: "center" }}
          />
          <span style={{ fontSize: 14 }}>{option.fullName}</span>
        </li>
      )}
      renderTags={(selected, getTagProps) => {
        if (selected.length === 0) return null;
        const firstDoctor = selected[0];
        const extraCount = selected.length - 1;
        return (
          <>
            <Tooltip title={firstDoctor.fullName} key={firstDoctor._id}>
              <Chip
                label={
                  extraCount > 0
                    ? `${firstDoctor.fullName} +${extraCount} more`
                    : firstDoctor.fullName
                }
                size="small"
                {...getTagProps({ index: 0 })}
                sx={{
                  fontSize: 12,
                  m: 0.3,
                  maxWidth: 180,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
            </Tooltip>
          </>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="medium"
          placeholder={value.length === 0 ? "Select doctor(s)" : ""}
          InputProps={{
            ...params.InputProps,
            sx: { fontSize: 13, px: 1, py: 0.5, alignItems: "center" },
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        width,
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          fontSize: 13,
          alignItems: "center",
          paddingTop: "4px",
          paddingBottom: "4px",
        },
        "& .MuiAutocomplete-input": { p: "4px 0", minWidth: 80 },
        "& .MuiAutocomplete-endAdornment": {
          top: "50%",
          right: 8,
          transform: "translateY(-50%)",
        },
        "& .MuiAutocomplete-popper .MuiAutocomplete-option": {
          fontSize: 14,
          py: 1,
        },
      }}
    />
  );
};

export default ReferredCases;
